import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Tournament, Player } from "../types";
import { apiRequest } from "../config/api";

type Props = {
  open: boolean;
  onClose: () => void;
  tournament: Tournament;
  players: Player[];
  onApplied: () => void;
  onNotify: (message: string) => void;
};

export default function TournamentReplacementDialog({
  open,
  onClose,
  tournament,
  players,
  onApplied,
  onNotify,
}: Props) {
  const [tab, setTab] = useState(0);
  const [history, setHistory] = useState<any[]>([]);
  const [waitlistTeams, setWaitlistTeams] = useState<any[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [teamMode, setTeamMode] = useState<"all" | "unplayed-only">("all");
  const [outgoingTeamId, setOutgoingTeamId] = useState("");
  const [teamSource, setTeamSource] = useState<"waitlist" | "manual">("waitlist");
  const [incomingWaitlistId, setIncomingWaitlistId] = useState("");
  const [manualPlayer1, setManualPlayer1] = useState<Player | null>(null);
  const [manualPlayer2, setManualPlayer2] = useState<Player | null>(null);
  const [manualTeamName, setManualTeamName] = useState("");
  const [teamReason, setTeamReason] = useState("");

  const [playerTeamId, setPlayerTeamId] = useState("");
  const [outgoingPlayerId, setOutgoingPlayerId] = useState("");
  const [incomingPlayer, setIncomingPlayer] = useState<Player | null>(null);
  const [playerReason, setPlayerReason] = useState("");

  const [preview, setPreview] = useState<any>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const selectedPlayerTeam = useMemo(
    () => (tournament.registeredTeams || []).find((t) => t.id === playerTeamId),
    [playerTeamId, tournament.registeredTeams],
  );

  const selectedWaitlistTeam = useMemo(
    () => waitlistTeams.find((w) => w.id === incomingWaitlistId) || null,
    [waitlistTeams, incomingWaitlistId],
  );

  const manualReady = !!manualPlayer1 && !!manualPlayer2 && manualPlayer1.id !== manualPlayer2.id;

  const getTeamLabel = (team: any) => team.teamName || `${team.player1Name || "P1"} & ${team.player2Name || "P2"}`;
  const getTeamNameById = (teamId?: string) => {
    if (!teamId) return "TBD";
    const t = (tournament.registeredTeams || []).find((team) => team.id === teamId);
    return t ? getTeamLabel(t) : teamId;
  };

  const fetchMeta = async () => {
    setLoadingMeta(true);
    try {
      const [historyRes, overviewRes] = await Promise.all([
        apiRequest(`/api/tournaments/${tournament.id}/replacements/history`),
        apiRequest(`/api/tournaments/${tournament.id}/registrations-overview`),
      ]);

      if (historyRes.ok) setHistory(await historyRes.json());
      else setHistory([]);

      if (overviewRes.ok) {
        const overview = await overviewRes.json();
        setWaitlistTeams(Array.isArray(overview?.waitlistTeams) ? overview.waitlistTeams : []);
      } else {
        setWaitlistTeams([]);
      }
    } catch (error) {
      console.error(error);
      setHistory([]);
      setWaitlistTeams([]);
    } finally {
      setLoadingMeta(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchMeta();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setConfirmChecked(false);
  }, [tab, outgoingTeamId, incomingWaitlistId, manualPlayer1, manualPlayer2, playerTeamId, outgoingPlayerId, incomingPlayer, teamMode]);

  const buildTeamPayload = () => {
    if (!outgoingTeamId) return null;

    if (teamSource === "waitlist") {
      if (!selectedWaitlistTeam) return null;
      const player1Id = selectedWaitlistTeam.player1?.id || selectedWaitlistTeam.player1Id;
      const player2Id = selectedWaitlistTeam.player2?.id || selectedWaitlistTeam.player2Id;
      if (!player1Id || !player2Id) return null;
      return {
        kind: "team",
        mode: teamMode,
        reason: teamReason,
        outgoingTeamId,
        incomingTeamId: selectedWaitlistTeam.id,
        incomingTeam: {
          id: selectedWaitlistTeam.id,
          player1Id,
          player2Id,
          player1Name: selectedWaitlistTeam.player1?.name,
          player2Name: selectedWaitlistTeam.player2?.name,
          teamName: selectedWaitlistTeam.teamName,
        },
      };
    }

    if (!manualReady) return null;
    const incomingTeamId = `${manualPlayer1!.id}-${manualPlayer2!.id}-${Date.now()}`;
    return {
      kind: "team",
      mode: teamMode,
      reason: teamReason,
      outgoingTeamId,
      incomingTeamId,
      incomingTeam: {
        id: incomingTeamId,
        player1Id: manualPlayer1!.id,
        player2Id: manualPlayer2!.id,
        player1Name: manualPlayer1!.name,
        player2Name: manualPlayer2!.name,
        teamName: manualTeamName || `${manualPlayer1!.name} & ${manualPlayer2!.name}`,
      },
    };
  };

  const buildPlayerPayload = () => {
    if (!playerTeamId || !outgoingPlayerId || !incomingPlayer) return null;
    return {
      kind: "player",
      reason: playerReason,
      teamId: playerTeamId,
      outgoingPlayerId,
      incomingPlayerId: incomingPlayer.id,
    };
  };

  const handlePreview = async () => {
    const payload = tab === 0 ? buildTeamPayload() : buildPlayerPayload();
    if (!payload) {
      onNotify("Please complete the replacement form first");
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiRequest(`/api/tournaments/${tournament.id}/replacements/preview`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Preview failed");
      setPreview(data);
      setConfirmChecked(false);
    } catch (error: any) {
      onNotify(error.message || "Preview failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApply = async () => {
    const payload = tab === 0 ? buildTeamPayload() : buildPlayerPayload();
    if (!payload) {
      onNotify("Please complete the replacement form first");
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiRequest(`/api/tournaments/${tournament.id}/replacements/apply`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Apply failed");
      onNotify("Replacement applied");
      await fetchMeta();
      onApplied();
      setPreview(null);
      setConfirmChecked(false);
    } catch (error: any) {
      onNotify(error.message || "Apply failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRollback = async (entryId: string) => {
    setSubmitting(true);
    try {
      const res = await apiRequest(`/api/tournaments/${tournament.id}/replacements/${entryId}/rollback`, {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Rollback failed");
      onNotify("Replacement rolled back");
      await fetchMeta();
      onApplied();
    } catch (error: any) {
      onNotify(error.message || "Rollback failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Replacements</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Replace Team" />
          <Tab label="Replace Player" />
          <Tab label="History" />
        </Tabs>

        {tab === 0 && (
          <Stack spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Outgoing Team</InputLabel>
              <Select value={outgoingTeamId} label="Outgoing Team" onChange={(e) => setOutgoingTeamId(e.target.value)}>
                {(tournament.registeredTeams || []).map((team) => (
                  <MenuItem key={team.id} value={team.id}>{getTeamLabel(team)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Replacement Source</InputLabel>
              <Select value={teamSource} label="Replacement Source" onChange={(e) => setTeamSource(e.target.value as any)}>
                <MenuItem value="waitlist">Promote from Waitlist</MenuItem>
                <MenuItem value="manual">Emergency Manual Team</MenuItem>
              </Select>
            </FormControl>

            {teamSource === "waitlist" ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Incoming Team (Waitlist)</InputLabel>
                <Select value={incomingWaitlistId} label="Incoming Team (Waitlist)" onChange={(e) => setIncomingWaitlistId(e.target.value)}>
                  {waitlistTeams.map((team: any) => (
                    <MenuItem key={team.id} value={team.id}>{team.teamName || `${team.player1?.name} & ${team.player2?.name}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <Autocomplete
                  options={players}
                  getOptionLabel={(p) => p.name}
                  value={manualPlayer1}
                  onChange={(_, v) => setManualPlayer1(v)}
                  renderInput={(p) => <TextField {...p} size="small" label="Manual Player 1" />}
                />
                <Autocomplete
                  options={players.filter((p) => p.id !== manualPlayer1?.id)}
                  getOptionLabel={(p) => p.name}
                  value={manualPlayer2}
                  onChange={(_, v) => setManualPlayer2(v)}
                  renderInput={(p) => <TextField {...p} size="small" label="Manual Player 2" />}
                />
                <TextField
                  size="small"
                  label="Team Name (optional)"
                  value={manualTeamName}
                  onChange={(e) => setManualTeamName(e.target.value)}
                />
              </>
            )}

            <FormControl size="small" fullWidth>
              <InputLabel>Effective Mode</InputLabel>
              <Select value={teamMode} label="Effective Mode" onChange={(e) => setTeamMode(e.target.value as any)}>
                <MenuItem value="all">Immediate on whole tournament</MenuItem>
                <MenuItem value="unplayed-only">Only unplayed fixtures</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Reason"
              value={teamReason}
              onChange={(e) => setTeamReason(e.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Team</InputLabel>
              <Select value={playerTeamId} label="Team" onChange={(e) => setPlayerTeamId(e.target.value)}>
                {(tournament.registeredTeams || []).map((team) => (
                  <MenuItem key={team.id} value={team.id}>{getTeamLabel(team)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedPlayerTeam && (
              <FormControl size="small" fullWidth>
                <InputLabel>Outgoing Player</InputLabel>
                <Select value={outgoingPlayerId} label="Outgoing Player" onChange={(e) => setOutgoingPlayerId(e.target.value)}>
                  <MenuItem value={selectedPlayerTeam.player1Id}>{selectedPlayerTeam.player1Name || selectedPlayerTeam.player1Id}</MenuItem>
                  <MenuItem value={selectedPlayerTeam.player2Id}>{selectedPlayerTeam.player2Name || selectedPlayerTeam.player2Id}</MenuItem>
                </Select>
              </FormControl>
            )}

            <Autocomplete
              options={players.filter((p) => p.id !== outgoingPlayerId)}
              getOptionLabel={(p) => p.name}
              value={incomingPlayer}
              onChange={(_, v) => setIncomingPlayer(v)}
              renderInput={(p) => <TextField {...p} size="small" label="Incoming Player" />}
            />

            <TextField
              size="small"
              label="Reason"
              value={playerReason}
              onChange={(e) => setPlayerReason(e.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        )}

        {tab === 2 && (
          <Stack spacing={1}>
            {loadingMeta && <Typography variant="body2" color="text.secondary">Loading history...</Typography>}
            {!loadingMeta && history.length === 0 && (
              <Typography variant="body2" color="text.secondary">No replacements yet.</Typography>
            )}
            {history.map((entry) => (
              <Box key={entry.id} sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap" }}>
                  <Chip size="small" label={String(entry.kind || "unknown").toUpperCase()} color={entry.kind === "rollback" ? "info" : "primary"} />
                  {entry.mode && <Chip size="small" label={entry.mode} variant="outlined" />}
                  {entry.rolledBack && <Chip size="small" color="warning" label="Rolled back" />}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block">
                  By {entry.createdBy || "admin"}
                </Typography>
                {entry.reason && <Typography variant="body2">{entry.reason}</Typography>}
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.75 }}>
                  {!entry.rolledBack && entry.kind !== "rollback" && (
                    <Button size="small" color="warning" onClick={() => handleRollback(entry.id)} disabled={submitting}>
                      Rollback
                    </Button>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}

        {preview && tab !== 2 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={700}>Impact Preview</Typography>
            {preview?.kind === "team" ? (
              <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                <Typography variant="caption">
                  Groups impacted: {preview?.impact?.impactedGroups?.length || 0}
                </Typography>
                <Typography variant="caption">
                  Group matches impacted: {preview?.impact?.impactedGroupMatches?.length || 0}
                </Typography>
                <Typography variant="caption">
                  Knockout matches impacted: {preview?.impact?.impactedKnockoutMatches?.length || 0}
                </Typography>

                {!!preview?.impact?.impactedGroups?.length && (
                  <Box>
                    <Typography variant="caption" fontWeight={700}>Groups</Typography>
                    <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                      {preview.impact.impactedGroups.map((g: any) => (
                        <Typography key={g.groupId} variant="caption">• {g.groupName || g.groupId}</Typography>
                      ))}
                    </Stack>
                  </Box>
                )}

                {!!preview?.impact?.impactedGroupMatches?.length && (
                  <Box>
                    <Typography variant="caption" fontWeight={700}>Group Matches</Typography>
                    <Stack spacing={0.25} sx={{ mt: 0.25, maxHeight: 120, overflow: "auto" }}>
                      {preview.impact.impactedGroupMatches.map((m: any, idx: number) => (
                        <Typography key={`${m.groupId}-${m.team1Id}-${m.team2Id}-${idx}`} variant="caption">
                          • {m.groupName || m.groupId}: {getTeamNameById(m.team1Id)} vs {getTeamNameById(m.team2Id)}
                          {m.courtNumber ? ` · Ct ${m.courtNumber}` : ""}
                          {m.played ? " · played" : " · unplayed"}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}

                {!!preview?.impact?.impactedKnockoutMatches?.length && (
                  <Box>
                    <Typography variant="caption" fontWeight={700}>Knockout Matches</Typography>
                    <Stack spacing={0.25} sx={{ mt: 0.25, maxHeight: 120, overflow: "auto" }}>
                      {preview.impact.impactedKnockoutMatches.map((m: any, idx: number) => (
                        <Typography key={`${m.bracket}-${m.matchId || m.matchNumber || idx}`} variant="caption">
                          • {m.bracket} · {m.round || "match"} #{m.matchNumber ?? "-"}
                          {m.courtNumber ? ` · Ct ${m.courtNumber}` : ""}
                          {m.played ? " · played" : " · unplayed"}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            ) : (
              <Typography variant="caption" component="pre" sx={{ m: 0, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(preview.impact || {}, null, 2)}
              </Typography>
            )}

            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} />}
              label={
                <Typography variant="caption" fontWeight={700}>
                  I reviewed this impact list and want to apply this replacement.
                </Typography>
              }
            />
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {tab !== 2 && (
          <>
            <Button variant="outlined" onClick={handlePreview} disabled={submitting}>Preview</Button>
            <Button variant="contained" color="warning" onClick={handleApply} disabled={submitting || !preview || !confirmChecked}>Apply Replacement</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
